import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subscription, timer } from 'rxjs';
import { ExerciseModel } from '@generativ/much-smarter-models';

import { ProblemService } from '../problem.service';
import { ParamsService } from '../../../../../shared/params.service';
import { ExerciseService } from '../exercise.service';
import { ProblemMultipleChoiceContainerComponent } from '../problem-mo-container/problem-multiple-choice-container.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap/tooltip';
import { SpinnerComponent } from '../../../../../shared/spinner/spinner.component';
import { TimePipe } from '../../../../../shared/pipes/time.pipe';

@Component({
    selector: 'app-problem',
    templateUrl: './problem.component.html',
    styleUrls: ['./problem.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgbTooltip, ProblemMultipleChoiceContainerComponent, SpinnerComponent, TimePipe]
})
export class ProblemComponent implements OnInit, OnDestroy, AfterViewInit {
  subscriptions: Subscription = new Subscription();
  lessonId: number;
  discardedOptions: string[];
  // Current problem time spent
  problemTimeSpent: number;
  isPersistence = false;

  hint: string;

  @ViewChild(ProblemMultipleChoiceContainerComponent, { static: true })
  private problemMCContainer: ProblemMultipleChoiceContainerComponent;

  readyToContinue = false;

  timer = timer(10, 1000);
  subscription: Subscription;
  currentTime = 0;

  getHintEnable = true;
  cutIncorrectEnable = true;
  stopTimerEnable = true;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private problemService: ProblemService,
              private paramsService: ParamsService,
              private exerciseService: ExerciseService) {
  }

  ngOnInit() {
    this.isPersistence = !!this.paramsService.getRouteDataById('persistence',
      this.activatedRoute.snapshot);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  get isHintEnable(): boolean {
    return this.getHintEnable && this.exerciseService.getSmartSavers().getHint > 0;
  }

  get isCutIncorrectEnable(): boolean {
    return this.cutIncorrectEnable && this.exerciseService.getSmartSavers().cutIncorrect > 0;
  }

  get isStopTimerEnable(): boolean {
    return this.stopTimerEnable && this.exerciseService.getSmartSavers().stopTimer > 0;
  }

  ngAfterViewInit() {
    this.problemTimeSpent = this.problemService.getProblemTimeSpent();
    this.subscription = this.timer.subscribe(
      time => {
        this.currentTime = time + this.problemTimeSpent;
      }
    );
  }

  handleNext() {
    this.getHintEnable = this.exerciseService.getSmartSavers().getHint > 0;
    this.cutIncorrectEnable = this.exerciseService.getSmartSavers().cutIncorrect > 0;
    this.stopTimerEnable = this.exerciseService.getSmartSavers().stopTimer > 0;
    if (this.problemMCContainer.problemInfo.isLastProblem) {
      const url = this.isPersistence ? '../conclude' : '../completed';
      this.router.navigate([url], {relativeTo: this.activatedRoute});
    } else {
      this.problemMCContainer.loadNextProblem();
    }
  }

  restartTimer() {

    this.problemTimeSpent = this.problemService.getProblemTimeSpent();
    this.currentTime = this.problemTimeSpent;
    this.subscription.unsubscribe();
    this.currentTime = 0;
    this.subscription = this.timer.subscribe(
      time => {
        this.currentTime = time + this.problemTimeSpent;
      }
    );
  }

  stopTimer() {
    this.subscription.unsubscribe();
  }

  onSkip() {
    this.exerciseService.skipProblem();
    this.problemMCContainer.loadNextProblem();
    this.restartTimer();
  }

  onCutIncorrect() {
    this.subscriptions.add(this.exerciseService.getIncorrectOptions().subscribe(
      (discardedOptions) => {
        this.discardedOptions = discardedOptions;
        this.cutIncorrectEnable = false;
      }
    ));
    this.exerciseService.useSmartSaver('cutIncorrect');
  }

  onGetHint() {
    this.subscriptions.add(this.exerciseService.getHint().subscribe(
      (hint) => {
        this.hint = hint;
        this.getHintEnable = false;
      }
    ));
    this.exerciseService.useSmartSaver('getHint');
  }

  onPauseTimer() {
    this.subscription.unsubscribe();
    this.stopTimerEnable = false;
    this.problemService.pauseProblemTimer();
    this.exerciseService.useSmartSaver('stopTimer');
  }

  get fasterWidth(): number {
    const currentExercise: ExerciseModel = this.exerciseService.currentExercise;
    const timeSpent = this.secondsToMinutes(this.problemService.getProblemTimeSpent());
    const showFaster = timeSpent < currentExercise.pacingGoal;
    return showFaster ? (timeSpent / currentExercise.pacingGoal) * 100 : 0;
  }

  get slowerWidth(): number {
    const currentExercise: ExerciseModel = this.exerciseService.currentExercise;
    const timeSpent = this.secondsToMinutes(this.problemService.getProblemTimeSpent());
    const showFaster = ( timeSpent > currentExercise.pacingGoal );
    return showFaster ? ((timeSpent - currentExercise.pacingGoal) / (currentExercise.pacingGoal)) * 100 : 0;
  }

  get exerciseProgressWidth(): number {
    const totalProblemCount = this.exerciseService.getCurrentExerciseProblemCount();
    const problemCountPlayed = totalProblemCount - this.exerciseService.problemsPendingLength;
    return (problemCountPlayed / totalProblemCount) * 100;
  }

  private secondsToMinutes(timeInSeconds: number): number {
    return timeInSeconds / 60;
  }
}
